import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Textarea,
  VStack,
  useToast,
  Text,
  Spinner,
  HStack,
  IconButton,
  Editable,
  EditableInput,
  EditablePreview,
  Input,
  Tooltip, // ⬅️ added
  useDisclosure, // ⬅️ added
  AlertDialog, // ⬅️ added
  AlertDialogBody, // ⬅️ added
  AlertDialogContent, // ⬅️ added
  AlertDialogFooter, // ⬅️ added
  AlertDialogHeader, // ⬅️ added
  AlertDialogOverlay, // ⬅️ added
  Link as ChakraLink, // ⬅️ for Markdown links
  List, // ⬅️ for Markdown lists
  ListItem, // ⬅️ for Markdown lists
  Code, // ⬅️ for inline code
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FiTrash2 } from "react-icons/fi"; // ⬅️ added
import AppLayout from "../components/layout/AppLayout";
import PageHeader from "../components/PageHeader";
import * as XLSX from "xlsx";
import PromptSuggestions from "../components/PromptSuggestions";

const md = {
  h1: (p) => <Heading size="md" mt={3} mb={2} {...p} />,
  h2: (p) => <Heading size="sm" mt={3} mb={2} {...p} />,
  h3: (p) => <Heading size="xs" mt={3} mb={1} {...p} />,
  p: (p) => <Text mb={2} {...p} />,
  ul: (p) => <List styleType="disc" pl={6} mb={2} {...p} />,
  ol: (p) => <List as="ol" styleType="decimal" pl={6} mb={2} {...p} />,
  li: (p) => <ListItem mb={1} {...p} />,
  code: (p) => <Code px={1} {...p} />,
  a: (p) => <ChakraLink color="green.600" isExternal {...p} />,
  blockquote: (p) => (
    <Box
      pl={3}
      borderLeft="4px solid"
      borderColor="gray.300"
      color="gray.700"
      {...p}
    />
  ),
};

const ChatAgent = () => {
  const [agent, setAgent] = useState("");
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionTitle, setSessionTitle] = useState("Untitled Session");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [uploadedDf, setUploadedDf] = useState(null);
  const toast = useToast();

  // delete dialog state (⬅️ added)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // bottom ref for auto-scroll (page-level)
  const bottomRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const agents = [
    { name: "Brand Manager", id: "brand_manager" },
    { name: "CPG Cofounder", id: "cpg_cofounder" },
    { name: "Category Manager", id: "category_manager" },
    { name: "Retail Buyer", id: "retail_buyer" },
    { name: "Distributor", id: "distributor" },
    { name: "Trade Promo Analyst", id: "trade_promo_analyst" },
    { name: "Commercial Strategy Director", id: "commercial_strategy_director" },
    { name: "Data Analyst", id: "data_analyst" },
    { name: "R&D (Product Formulator)", id: "product_formulator" },
    { name: "DTC Strategist", id: "dtc_strategist" },
  ];

  const fetchSessions = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/chat-sessions/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setSessions(Array.isArray(data) ? data : []); // Prevent filter error
  };

  const fetchHistory = async (session_id) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/chat-history/${session_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    const formatted = data
      .map((entry) => [
        { role: "user", text: entry.user_message },
        { role: "agent", text: entry.agent_response || "No response received." },
      ])
      .flat();
    setChatHistory(formatted);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(firstSheet);
      setUploadedDf(parsed);
      toast({
        title: "File uploaded",
        description: `Parsed ${parsed.length} rows.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (!agent || !message) {
      toast({
        title: "Please select an agent and enter a message.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setTyping(true);

    try {
      const token = localStorage.getItem("token");
      const productData =
        agent === "data_analyst" && uploadedDf ? { uploaded_df: uploadedDf } : {};

      const res = await fetch(`${API_BASE}/api/chat-agent/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          agent,
          product_data: productData,
          user_message: message,
          session_id: sessionId,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        const agentReply =
          data.tagline || data.romance_copy || data.callout_text
            ? `✨ Tagline: ${data.tagline || "N/A"}\n\n💫 Romance: ${
                data.romance_copy || "N/A"
              }\n\n🔥 Callout: ${data.callout_text || "N/A"}`
            : data.clean_output || "No response received.";

        setChatHistory((prev) => [
          ...prev,
          { role: "user", text: message },
          { role: "agent", text: agentReply },
        ]);
        setSessionId(data.session_id);
        if (!sessionId) fetchSessions();
        setMessage("");
      } else {
        throw new Error(data?.detail || "Something went wrong");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    setLoading(false);
    setTyping(false);
  };

  const renameSession = async (newTitle) => {
    const token = localStorage.getItem("token");
    await fetch(`${API_BASE}/chat-sessions/${sessionId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ session_title: newTitle }),
    });
    setSessionTitle(newTitle);
    fetchSessions();
  };

  const handleSessionClick = async (session) => {
    setSessionId(session.session_id);
    setSessionTitle(session.session_title);
    await fetchHistory(session.session_id);
  };

  // ⬇️ added: delete helper
  const deleteSession = async (sid) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/chat-sessions/${sid}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Delete failed (${res.status})`);
    }
  };

  // ⬇️ added: confirm + perform delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteSession(deleteTarget.session_id);
      setSessions((prev) =>
        prev.filter((s) => s.session_id !== deleteTarget.session_id)
      );
      if (sessionId === deleteTarget.session_id) {
        setSessionId(null);
        setSessionTitle("Untitled Session");
        setChatHistory([]);
      }
      toast({ title: "Thread deleted", status: "success", duration: 2000 });
      onClose();
    } catch (e) {
      toast({ title: "Delete failed", description: e.message, status: "error" });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto scroll to bottom (page scroll, not inner box)
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory, loading, typing]);

  const filteredSessions = Array.isArray(sessions)
    ? sessions.filter((s) => s.agent === agent)
    : [];

  return (
    <AppLayout>
      {/* Use minH instead of fixed h so the page can grow and scroll */}
      <Flex minH="calc(100vh - 80px)">
        {/* LEFT SIDEBAR – Sessions */}
        <Box
          w="250px"
          bg="gray.100"
          p={4}
          overflowY="auto"
          borderRight="1px solid #CBD5E0"
        >
          <Heading size="md" mb={4}>
            Sessions
          </Heading>

          {filteredSessions.map((session) => (
            <HStack key={session.session_id} mb={2} align="stretch">
              <Button
                variant={session.session_id === sessionId ? "solid" : "ghost"}
                colorScheme="green"
                size="sm"
                onClick={() => handleSessionClick(session)}
                width="100%"
                justifyContent="flex-start"
                textAlign="left"
                whiteSpace="normal"
                height="auto"
                paddingY={2}
                paddingX={3}
              >
                {session.session_title}
              </Button>
              <Tooltip label="Delete">
                <IconButton
                  aria-label="Delete session"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDeleteTarget(session);
                    onOpen();
                  }}
                />
              </Tooltip>
            </HStack>
          ))}

          {/* confirm dialog */}
          <AlertDialog isOpen={isOpen} onClose={onClose}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader>Delete this chat?</AlertDialogHeader>
                <AlertDialogBody>
                  This will remove the thread and its messages.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button onClick={onClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    ml={3}
                    onClick={confirmDelete}
                    isLoading={deleting}
                  >
                    Delete
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </Box>

        {/* RIGHT PANEL – Chat */}
        <Flex
          direction="column"
          flex="1"
          p={6}
          minW={0}
          bg="white"
          overflow="hidden"
        >
          {/* Header */}
          <Box w="100%" maxW="900px" mx="auto">
            <PageHeader
              title="Chat with a CPG Advisor"
              subtitle="Ask questions, upload data, and get instant insights"
            />
          </Box>

          {/* Agent selection + helpers */}
          <Box w="100%" maxW="900px" mx="auto" mt={4}>
            <Select
              placeholder="Select an Advisor"
              value={agent}
              onChange={(e) => {
                setAgent(e.target.value);
                setChatHistory([]);
                setSessionId(null);
                setUploadedDf(null);
              }}
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>

            {agent && (
              <Box mt={3}>
                <PromptSuggestions
                  agentId={agent}
                  onPromptSelect={(prompt) => setMessage(prompt)}
                />
              </Box>
            )}

            {agent === "data_analyst" && (
              <Box mt={3}>
                <Input
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileUpload}
                />
              </Box>
            )}

            {sessionId && (
              <Box mt={4}>
                <Editable
                  defaultValue={sessionTitle}
                  fontWeight="semibold"
                  onSubmit={renameSession}
                >
                  <HStack>
                    <EditablePreview />
                    <EditableInput />
                    <IconButton
                      size="sm"
                      icon={<EditIcon />}
                      aria-label="Edit title"
                    />
                  </HStack>
                </Editable>
              </Box>
            )}
          </Box>

          {/* MAIN CHAT AREA – now grows with the page, no inner scroll */}
          <Box
            mt={4}
            w="100%"
            maxW="900px"
            mx="auto"
            border="1px solid #CBD5E0"
            borderRadius="lg"
            p={4}
            bg="gray.50"
          >
            {chatHistory.map((entry, idx) => (
              <Box
                key={idx}
                mb={4}
                bg={entry.role === "user" ? "white" : "green.50"}
                p={4}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Text fontWeight="bold" mb={1}>
                  {entry.role === "user" ? "You" : "Agent"}
                </Text>
                <Box mt={1}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>
                    {entry.text || ""}
                  </ReactMarkdown>
                </Box>
              </Box>
            ))}

            {typing && (
              <Box bg="green.50" p={4} borderRadius="lg" boxShadow="sm">
                <Text fontWeight="bold">Agent</Text>
                <HStack mt={2} spacing={2}>
                  <Text>Typing</Text>
                  <Spinner size="xs" />
                </HStack>
              </Box>
            )}

            {/* invisible anchor for auto-scroll */}
            <Box ref={bottomRef} />
          </Box>

          {/* INPUT AREA */}
          <Box w="100%" maxW="900px" mx="auto" mt={4} pb={1}>
            <HStack align="flex-end">
              <Textarea
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                resize="vertical"
              />
              <Button
                colorScheme="green"
                onClick={handleSubmit}
                isLoading={loading}
                minW="90px"
              >
                Send
              </Button>
            </HStack>
          </Box>
        </Flex>
      </Flex>
    </AppLayout>
  );
};

export default ChatAgent;
